define([
	"dojo/_base/declare",
	"csi",
	"csi/_base/underscore",
	"csi/prefs/_base",
	"csi/resolver",
	"csi/sessionConstants",
	"csi/xml"
], function(declare, csi, _, csiPrefs, resolver, sessionConstants, xml) {

	var EMPTY_OBJ = {};

	/**
	 * Return value for given custom view URL - do not store "centric:"
	 * @param {cnl} urlView
	 * @return {cnl | undefined}
	 */
	function getViewValue(urlView) {
		return xml.isValidURL(urlView) ? urlView : undefined;
	}

	return declare(null, {
		// Boolean: indicates if editing defaults - if so, then copy preferences while editing and assign only when writing changes
		bEdit: false,

		// Object: map of watches - these should persist across
		watches: null,

		// Object[]: array of changes to defaults
		_changes: null,

		// Object: mapping of party URL to preferences URL, id, and default views map
		_partyPreferences: null,

		// Object: mapping of role URL to role defaults map
		_roles: null,

		// Object: site defaults map
		_site: null,

		// Object: user defaults map
		_user: null,

		constructor: function(/*Boolean*/bEdit) {
			this.bEdit = !!bEdit;
			this._partyPreferences = {};
			this._roles = {};
		},

		/**
		 * return true if view has user defined system wide default cv
		 * @param {string*} idContext 
		 */
		hasSystemWideDefault: function(idContext) {
			const url = this.get(idContext, csi.EMPTY_URL);
			return xml.isValidURL(url);
		},

		setRolePreferences: function(/*Node*/node) {
			// summary: track role preferences
			this._roles[node.__Parent__] = node ? this._addPreferences(node.__Parent__, node, "SiteDefaultViews") : null;
		},

		setSitePreferences: function(/*Node*/node) {
			// summary: track site preferences
			this._site = this._addPreferences(csi.EMPTY_URL, node, "SiteDefaultViews");
		},

		setUserPreferences: function(/*Node*/node) {
			// summary: track user preferences
			this._user = node ? this._addPreferences(node.__Parent__, node, "DefaultViews") : null;
		},

		get: function(/*String*/idContext, /*String?*/urlParty, /*Boolean?*/bExplicit) {
			// summary: return default custom view for given context
			//		if urlParty is not specified, then consider user, role and site defaults; otherwise consider just party defaults
			//		if bExplicit, then return undefined if no default found and do not check other parties (site/role) for defaults
			//
			// Order of precedence for:
			// 		party - user, then role, then site preferences
			//		context - node context (i.e. Style Type), then action/view context (for popup views), then default
			//		party & context - check node & view context for each party before testing next party type
			const keyObj = csiPrefs.decomposeKey(idContext);
			const v = keyObj.view;
			const vc = keyObj.viewContext;
			const nc = keyObj.nodeContext;
			let rd = this._roles[urlParty];
			let sd, ud, url;

			if (rd) {
				sd = bExplicit ? null : this._site;
			} else if (urlParty == csi.EMPTY_URL) {
				sd = this._site;
			} else {
				sd = bExplicit ? null : this._site;
				rd = bExplicit ? null : this._roles[sessionConstants.roleUrl];
				ud = this._user;
			}

			ud = ud || EMPTY_OBJ;
			rd = rd || EMPTY_OBJ;
			sd = sd || EMPTY_OBJ;

			if (!nc && !vc) {
				// if no view or node context, then only need to check explicit defaults
				url = ud[idContext] || rd[idContext] || sd[idContext];
			} else if (!nc || !vc) {
				// check default for view key and then shared defaults if required - a user default for the view as a whole will override
				// the role default for that view, even if there is a role default for the particular context. It works similarly for role defaults
				// vs. site defaults
				url = ud[idContext] || ud[v] || rd[idContext] || rd[v] || sd[idContext] || sd[v];
			} else {
				// check defaults for view key with view or node context, including shared defaults if required
				var vvc = csiPrefs.composeKey(v, vc);
				var vnc = csiPrefs.composeKey(v, null, nc);
				url = ud[idContext] || ud[vvc] || rd[idContext] || rd[vvc] || sd[idContext] || sd[vvc] || ud[vnc] || ud[v] || rd[vnc] || rd[v] || sd[vnc] || sd[v];
			}

			return url || (bExplicit ? undefined : csi.EMPTY_URL);
		},

		/**
		 * get default custom views for the subtyped views, including the global default
		 * @param {string} idContext 
		 * @param {cnl|null} partyUrl - cnl of the party, pass null to get the global level list 
		 * @returns {Record<cnl, string[]>}
		 */
		getSubtypedList(idContext, partyUrl) {
			const {_roles, _site} = this;
			const defaultViews = partyUrl ? _roles[partyUrl] : _site;
			const keyObj = csiPrefs.decomposeKey(idContext);
			const containerKey = keyObj.view;

			const viewMap = new Map();

			function isRoleSubtypeDefault(viewKey) {
				const { view} = csiPrefs.decomposeKey(viewKey);

				// ignore the ones that have different view owners
				return view !== containerKey;
			}

			function isSystemWideSubtypeDefault(viewKey) {
				// ignore the un-relevant ones
				return !viewKey.includes(containerKey) || viewKey === containerKey; 
			}

			const filter = partyUrl ?  isRoleSubtypeDefault : isSystemWideSubtypeDefault;

			// add the site level first, we may not set it up yet
			const siteDefaultViewCnl = _site[containerKey]||"";
			viewMap[siteDefaultViewCnl] = [];

			for (const [viewKey, viewCnl] of Object.entries(defaultViews)) {
				if (filter(viewKey)) {
					continue;
				}

				const subtype = csiPrefs.decomposeKey(viewKey).nodeContext;

				if (!!partyUrl && !subtype) {
					// if role based view has its own default for all types, override the one from the site level default
					delete viewMap[siteDefaultViewCnl];
					viewMap[viewCnl] = [];
					continue;
				}

				viewMap[viewCnl] ||= [];
				viewMap[viewCnl].push(subtype);
			}

			return viewMap;
		},

		notify: function(/*String[]*/contexts) {
			// summary: notify watchers of given contexts
			_.each(contexts, function(idContext) {
				var url = this.get(idContext);

				_.each(this.watches?.[idContext], function(handle) {
					handle.func(url);
				});
			}, this);
		},

		remove: function(/*String*/urlView, /*String*/urlParty) {
			// summary: remove view from all defaults
			var b;

			_.each(this._partyPreferences, function(p, url) {
				if (!urlParty || (url == urlParty)) {
					_.each(csi.objectRemoveValue(p.obj, urlView), function(key) {
						this._addChange(url, key);
						b = true;
					}, this);
				}
			}, this);

			return b;
		},

		set: function(/*String*/idContext, /*String*/urlView, /*String*/urlParty) {
			// summary: add mapping to site/role/user preferences
			const prefs = this._partyPreferences[urlParty || sessionConstants.userUrl];
			const { obj } = prefs;
			let value;

			// determine if key being set is already the default for the given context - if so, then remove the key rather than add it
			obj[idContext] = null;

			if (urlView != getViewValue(this.get(idContext, urlParty))) {
				obj[idContext] = value = urlView;
			}

			this._addChange(urlParty, idContext, value);

			// remove keys with the same value if the given context is a default for the given key -
			// since we are changing the object as we iterate through it, don't use _.each(obj,*)
			_.each(_.keys(obj), function(key) {
				value = getViewValue(obj[key]);

				if ((key != idContext) && (value == urlView)) {
					obj[key] = null;

					if (this.get(key, urlParty) != urlView) {
						obj[key] = value;
					} else {
						this._addChange(urlParty, key);
					}
				}
			}, this);
		},

		trackChanges: function() {
			// summary: indicate that any changes to defaults should be tracked so they can be later published
			this._changes = [];
		},

		write: function(/*Writer*/w) {
			// summary: write changes as XML operations, and if editing, make sure session preferences are updated with changes;
			// return list of keys (contexts) that were changed
			var bCannotCache;
			var cache;
			var changedKeys = {};

			cache = w.cache;
			bCannotCache = w.bCannotCache;
			w.cache = resolver.m_cache;
			w.bCannotCache = false;

			_.each(this._changes, function(obj) {
				var prefs = this[obj.url];
				var cn = w.changeNode(prefs.url);

				changedKeys[obj.key] = true;

				if (obj.value) {
					cn.changeAttributeKeys(prefs.id, xml.ATTR_REFMAP, csi.objectNew(obj.key, obj.value));
				} else {
					cn.removeAttributeKeys(prefs.id, xml.ATTR_REFMAP, [obj.key]);
				}
			}, this._partyPreferences);

			if (cache) {
				w.cache = cache;
				w.bCannotCache = bCannotCache;
			}

			this._changes = null;

			return _.keys(changedKeys);
		},

		_addChange: function(/*String*/urlParty, /*String*/idContext, /*String?*/urlView) {
			// summary: track change
			if (this._changes) {
				this._changes.push({
					url: urlParty,
					key: idContext,
					value: urlView,
				});
			}
		},

		_addPreferences: function(/*String*/urlParty, /*Node*/node, /*String*/id) {
			// summary: store preferences
			if (!node) {
				return;
			}
			var obj = this.bEdit ? _.clone(node[id]) : node[id];

			this._partyPreferences[urlParty] = {
				url: node.$URL,
				id: id,
				obj: obj
			};

			return obj;
		}
	});
});